// Libraries
import { useBreakpointValue } from '@chakra-ui/react';
import {
  ColumnFiltersState,
  FilterFn,
  SortingState,
  getCoreRowModel,
  getExpandedRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useState } from 'react';
// Utils
import { fuzzyFilter, caseInsensitiveFilter } from '@helpers/index';
// Types
import { useTableProps } from './useTable.types';

/**
 * useTable hook.
 *
 * @template TableBody - The type of data in the table body.
 * @template ColumnNames - The type of column names.
 * @param {Object} props - The hook props.
 * @param {TableBody[]} props.data - The table data.
 * @param {ColumnDef<TableBody, unknown>[]} props.columns - The table columns.
 * @param {ColumnVisibilityMap<ColumnNames>} [props.columnVisibilityMap={}] - The map of column names to visibility breakpoints.
 * @param {FilterOptionsMap<ColumnNames>} props.columnFiltersMap - The map of column names to filter options.
 * @param {ColumnFiltersState} [props.initialColumnFiltersState=[]] - The initial state for column filters.
 * @param {boolean} [props.isRowExpanded=false] - Indicates whether row expansion is active.
 * @param {boolean} [props.isRowSelectionActive=false] - Indicates whether row selection is active.
 * @param {SortingState} [props.defaultSorting=false] - Object to define the default sorting state for the table.
 * @returns {Object} - The table instance and column filters map.
 *   @property {TableInstance<TableBody>} table - The table instance.
 *   @property {Object} columnFiltersMap - The map of column names to filter options.
 */
export const useTable = <TableBody, ColumnNames extends string>({
  data,
  columns,
  columnVisibilityMap = {},
  columnFiltersMap,
  initialRowSelection,
  initialColumnFiltersState = [],
  isRowExpanded = false,
  isRowSelectionActive = false,
  defaultSorting = [],
}: useTableProps<TableBody, ColumnNames>) => {
  const columnVisibility = useBreakpointValue(columnVisibilityMap, { ssr: false });

  // Create States for the table
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(initialColumnFiltersState);
  const [globalFilter, setGlobalFilter] = useState('');
  const [rowSelection, setRowSelection] = useState(initialRowSelection || {});
  const [sorting, setSorting] = useState<SortingState>(defaultSorting);

  // Check if are filters incoming
  const isFilter = !!columnFiltersMap;

  // Use the `useReactTable` hook to create the table instance
  const table = useReactTable({
    data,
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
      caseInsensitiveFilter: caseInsensitiveFilter,
    },
    state: {
      columnVisibility,
      columnFilters,
      globalFilter,
      rowSelection,
      sorting,
    },
    enableColumnResizing: true,
    enableColumnFilters: isFilter,
    enableRowSelection: isRowSelectionActive,
    columnResizeMode: 'onChange',
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getRowCanExpand: () => isRowExpanded,
    getExpandedRowModel: getExpandedRowModel(),
    globalFilterFn: fuzzyFilter as FilterFn<TableBody>,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  });

  return { table, columnFiltersMap, setRowSelection };
};
